import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@repo/shared/domain';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getPartnerCompanyTask, getPartnerCompanyDocuments } from 'src/api/partner';
import { constructQuery, getVerifyPermission } from '@repo/shared/helpers';
import { Spinner } from '@repo/components';
import { NotFound } from '@repo/components';
import { TaskViewer } from '@repo/components';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/partner_/$partnerId/tasks/$taskId')({
	component: RouteComponent,
});

function RouteComponent() {
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const { partnerId, taskId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	useBreadcrumbLevel(1, 'Tasks', `/partner/${partnerId}/tasks`);
	useBreadcrumbLevel(2, 'Task Details');

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const {
		data: task,
		isLoading: taskLoading,
		error: taskError,
	} = useQuery({
		queryKey: ['partner-company-task', partnerId, taskId],
		queryFn: () => getPartnerCompanyTask(partnerId, taskId),
		enabled: !userLoading && !!authenticatedUser,
	});

	const {
		data: documentsResponse,
		isLoading: documentsLoading,
	} = useQuery({
		queryKey: ['partner-company-documents', partnerId],
		queryFn: () => getPartnerCompanyDocuments(partnerId, constructQuery({ noPagination: 'true' })),
		enabled: !userLoading && !!authenticatedUser && !!task,
	});

	const documents = documentsResponse?.data || [];
	const filteredDocuments = task?.documentIds ? documents.filter((d) => task.documentIds?.includes(d._id)) : [];

	const isLoading = userLoading || taskLoading || documentsLoading;

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	if (!task || taskError) {
		return (
			<NotFound>
				<div className="w-[300px]">
					<NotFound.Illustration src="/public/empty_box.svg" />
				</div>
				<NotFound.Label>We couldn't find this task. Maybe it has been deleted?</NotFound.Label>
				<NotFound.Actions>
					<NotFound.Actions.Action onClick={() => navigate({ to: `/partner/${partnerId}/tasks` })} variant="outlined">
						Return to tasks
					</NotFound.Actions.Action>
				</NotFound.Actions>
			</NotFound>
		);
	}

	const handleUpdate = async () => {
		// Invalidate queries to refetch data
		await queryClient.invalidateQueries({
			queryKey: ['partner-company-task', partnerId, taskId],
		});
		await queryClient.invalidateQueries({
			queryKey: ['partner-company-tasks', partnerId],
		});
	};

	const handleClose = () => {
		navigate({ to: `/partner/${partnerId}/tasks` });
		return null;
	};

	const handleOpenDocument = (documentId: string) => {
		navigate({ to: `/partner/${partnerId}/documents/${documentId}` });
		return null;
	};

	// Set task permissions - for partner portal, when tasks are done they are read-only
	task.executePermission = {
		isAllowed: task.status === 'todo',
		denyReason: task.status === 'todo' ? undefined : 'You are viewing this task in read-only mode',
	};

	return (
		<TaskViewer
			task={task}
			documents={filteredDocuments}
			onUpdate={handleUpdate}
			onClose={handleClose}
			onOpenDocument={handleOpenDocument}
			permissions={{
				taskDo: verifyPermission([PERMISSIONS.partnerCompanyTasksDo]),
				taskDelete: undefined,
			}}
		/>
	);
}
